import React, { useState } from 'react';
import { 
    Box, 
    List, 
    ListItem, 
    ListItemIcon, 
    ListItemText, 
    Typography,
    Collapse,
    Chip,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    ElectricBolt,
    People,
    Security,
    Receipt,
    CalendarMonth,
    Settings,
    ExpandLess,
    ExpandMore,
    ChevronLeft,
    ChevronRight,
    FiberManualRecord,
    Equalizer,
    AttachMoney,
    Schedule,
    TrendingUp,
    Calculate,
    PriceCheck,
    Groups,
    Notifications,
    Description,
    Campaign,
    Business,
    Badge,
    CreditCard,
    Bolt,
    LocalOffer,
    SystemUpdate,
    CloudDownload,
    DirectionsCar,
    Router,
    Payment,
    AccountBalanceWallet,
    Psychology,
    Storefront
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslate, usePermissions, useGetIdentity } from 'react-admin';
import { hasPermission as checkPermission } from '../../utils/hasPermission';
import { useKioskAlertSummary } from '../kiosk-alerts/useKioskAlertSummary';
import { useKioskNeedsActionSummary } from '../../hooks/useKioskNeedsActionSummary';
import { needsActionBadgeCount } from '../transactions/kiosk/needsAction';

interface MenuItem {
    name: string;
    label: string;
    icon: React.ReactElement;
    path?: string;
    badge?: number | string;
    badgeColor?: string;
    children?: MenuItem[];
    requiredPermission?: string;
    // If set, item is visible when the actor has ANY one of the listed
    // permissions. Takes precedence over requiredPermission when both exist.
    // Used for landing pages (e.g. Reports Dashboard) that aggregate multiple
    // features — any single feature permission should be enough to see the link.
    requiredAnyPermission?: string[];
    // If true, hide for vendor-scoped admins (actors with non-empty vendorIds).
    // Used for platform-only pages (e.g. kiosk releases).
    hideForVendorScoped?: boolean;
}

// Granular per-report permissions — referenced by menu items AND by the
// Reports Dashboard "any of" gate so the sidebar link stays visible when a
// user has at least one individual report permission.
const ALL_REPORT_READ_PERMISSIONS = [
    'reports:revenue:read',
    'reports:transactions:read',
    'reports:sessions:read',
    'reports:charger-utilization:read',
    'reports:energy-consumption:read',
    'reports:peak-usage:read',
    'reports:user-activity:read',
    'reports:vendor-performance:read',
    'reports:site-comparison:read',
    'reports:kiosk:read',
];

interface CustomMenuProps {
    onCollapseChange?: (collapsed: boolean) => void;
    onNavigate?: () => void;
}

export const CustomMenu = ({ onCollapseChange, onNavigate }: CustomMenuProps = {}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const { identity } = useGetIdentity();
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;
    const needsActionSummary = useKioskNeedsActionSummary(checkPermission(permissions as string[] | null | undefined, 'kiosk-transactions:read:all'));
    const needsActionCount = needsActionSummary === null ? 0 : needsActionBadgeCount(needsActionSummary);
    const canSeeKioskAlerts = checkPermission(permissions as string[] | null | undefined, 'kiosk-alerts:read:all');
    const kioskAlertSummary = useKioskAlertSummary(canSeeKioskAlerts);
    const openKioskAlerts = kioskAlertSummary !== null && kioskAlertSummary.openTotal > 0 ? kioskAlertSummary.openTotal : undefined;
    const [collapsed, setCollapsed] = useState(false);
    const [openItems, setOpenItems] = useState<string[]>([]);
    
    // Debug permissions
    React.useEffect(() => {
        console.log('User permissions:', permissions);
    }, [permissions]);

    // Auto-expand parent menu item that contains the current active page
    React.useEffect(() => {
        const currentPath = location.pathname;
        const findParentForPath = (items: MenuItem[]): string | null => {
            for (const item of items) {
                if (item.children) {
                    for (const child of item.children) {
                        if (child.path && (currentPath === child.path || currentPath.startsWith(child.path + '/'))) {
                            return item.name;
                        }
                    }
                }
            }
            return null;
        };

        const parentName = findParentForPath(menuItems);
        if (parentName && !openItems.includes(parentName)) {
            setOpenItems([parentName]);
        }
    }, [location.pathname]);
    
    const handleCollapseToggle = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        onCollapseChange?.(newCollapsed);
    };

    const menuItems: MenuItem[] = [
        {
            name: 'dashboard',
            label: translate('dashboard.title'),
            icon: <DashboardIcon />,
            path: '/',
            requiredPermission: 'sites:read:all',
        },
        {
            name: 'operations',
            label: translate('menu.operations'),
            icon: <ElectricBolt />,
            children: [
                {
                    name: 'sites',
                    label: translate('resources.sites.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/sites',
                    requiredPermission: 'sites:read:all',
                },
                {
                    name: 'load-groups',
                    label: translate('resources.load-groups.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/load-groups',
                    requiredPermission: 'load-groups:read:all',
                },
                {
                    name: 'lms-templates',
                    label: translate('resources.lms-templates.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/lms-templates',
                    requiredPermission: 'lms-templates:read:all',
                },
                {
                    name: 'ocpp-templates',
                    label: translate('resources.ocpp-templates.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/ocpp-templates',
                    requiredPermission: 'ocpp-templates:read:all',
                },
                {
                    name: 'ocpp-servers',
                    label: translate('resources.ocpp-servers.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/ocpp-servers',
                    requiredPermission: 'ocpp-servers:read:all',
                },
            ],
        },
        {
            name: 'charger-management',
            label: translate('menu.chargerManagement') || 'Charger Management',
            icon: <DirectionsCar />,
            children: [
                {
                    name: 'chargers',
                    label: translate('resources.chargers.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/chargers',
                    requiredPermission: 'chargers:read:all',
                },
                {
                    name: 'charger-types',
                    label: translate('resources.charger-types.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/charger-types',
                    requiredPermission: 'charger-types:read:all',
                },
                {
                    name: 'charger-firmware',
                    label: translate('menu.chargerFirmware') || 'Charger Firmware',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/charger-firmware',
                    requiredPermission: 'charger-firmware:read:all',
                },
                {
                    name: 'firmware-updates',
                    label: translate('menu.firmwareUpdates') || 'Firmware Updates',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/firmware-updates',
                    requiredPermission: 'firmware-updates:read:all',
                },
            ],
        },
        {
            name: 'charging-management',
            label: translate('menu.chargingManagement') || 'Charging Management',
            icon: <Bolt />,
            children: [
                {
                    name: 'active-sessions',
                    label: translate('menu.activeSessions') || 'Active Sessions',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/active-sessions',
                    requiredPermission: 'active-sessions:read:all',
                },
                {
                    name: 'charging-sessions',
                    label: translate('menu.chargingHistory') || 'Charging History',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/charging-sessions',
                    requiredPermission: 'charging-sessions:read:all',
                },
            ],
        },
        {
            name: 'vendor-management',
            label: translate('menu.vendorManagement') || 'Vendor Management',
            icon: <Business />,
            children: [
                {
                    name: 'vendors',
                    label: translate('resources.vendors.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/vendors',
                    requiredPermission: 'vendors:read:all',
                },
                {
                    name: 'rfid-users',
                    label: translate('resources.rfid-users.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/rfid-users',
                    requiredPermission: 'rfid-users:read:all',
                },
                {
                    name: 'rfid-cards',
                    label: translate('resources.rfid-cards.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/rfid-cards',
                    requiredPermission: 'rfid-cards:read:all',
                },
            ],
        },
        {
            name: 'kiosk',
            label: translate('menu.kiosk') || 'Kiosk',
            icon: <Storefront />,
            badge: openKioskAlerts,
            badgeColor: '#D32F2F',
            children: [
                {
                    name: 'kiosk-devices',
                    label: translate('resources.kiosk-devices.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/kiosk-devices',
                    requiredPermission: 'kiosk-devices:read:all',
                },
                {
                    name: 'kiosk-alerts',
                    label: translate('menu.kioskAlerts'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/kiosk-alerts',
                    requiredPermission: 'kiosk-alerts:read:all',
                    badge: openKioskAlerts,
                    badgeColor: '#D32F2F',
                },
                {
                    name: 'kiosk-releases',
                    label: translate('menu.kioskReleases'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/kiosk-releases',
                    requiredPermission: 'kiosk-releases:read:all',
                    hideForVendorScoped: true,
                },
                {
                    name: 'kiosk-release-key',
                    label: translate('menu.kioskReleaseKey'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/kiosk-release-key',
                    requiredPermission: 'kiosk-releases:read:all',
                    hideForVendorScoped: true,
                },
                {
                    name: 'kiosk-groups',
                    label: translate('menu.kioskGroups'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/kiosk-groups',
                    requiredPermission: 'kiosk-releases:read:all',
                    hideForVendorScoped: true,
                },
            ],
        },
        {
            name: 'vehicle-catalog',
            label: translate('menu.vehicleCatalog') || 'Vehicle Catalog',
            icon: <DirectionsCar />,
            children: [
                {
                    name: 'vehicle-brands',
                    label: translate('resources.vehicle-brands.name') || 'Vehicle Brands',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/vehicle-brands',
                    requiredPermission: 'vehicle-catalog:read:all',
                },
                {
                    name: 'vehicle-models',
                    label: translate('resources.vehicle-models.name') || 'Vehicle Models',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/vehicle-models',
                    requiredPermission: 'vehicle-catalog:read:all',
                },
            ],
        },
        {
            name: 'users',
            label: translate('menu.userManagement'),
            icon: <People />,
            children: [
                {
                    name: 'app-users',
                    label: translate('resources.app-users.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/app-users',
                    requiredPermission: 'app-users:read:all',
                },
                {
                    name: 'account-deletion-requests',
                    label: translate('menu.appAccountDeletion') || 'App Account Deletion',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/account-deletion-requests',
                    requiredPermission: 'account_deletion_requests:process:all',
                },
                {
                    name: 'backend-users',
                    label: translate('resources.backend-users.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/backend-users',
                    requiredPermission: 'users:read:all',
                },
                {
                    name: 'user-groups',
                    label: translate('menu.userGroups'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/user-groups',
                    requiredPermission: 'user-groups:read:all',
                },
            ],
        },
        {
            name: 'transactions',
            label: translate('menu.transactions'),
            icon: <Receipt />,
            children: [
                {
                    name: 'app-transactions',
                    label: translate('menu.appTransactions') || 'App Transactions',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/app-transactions',
                    requiredPermission: 'app-transactions:read:all',
                },
                {
                    name: 'app-topups',
                    label: translate('menu.appTopUps') || 'Top-Ups',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/app-topups',
                    requiredPermission: 'app-topups:read:all',
                },
                {
                    name: 'rfid-transactions',
                    label: translate('menu.rfidTransactions') || 'RFID Transactions',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/rfid-transactions',
                    requiredPermission: 'rfid-transactions:read:all',
                },
                {
                    name: 'kiosk-transactions',
                    label: translate('menu.kioskTransactions') || 'Kiosk Transactions',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/kiosk-transactions',
                    requiredPermission: 'kiosk-transactions:read:all',
                    badge: needsActionCount > 0 ? needsActionCount : undefined,
                    badgeColor: '#D32F2F',
                },
                {
                    name: 'kiosk-settlements',
                    label: translate('menu.kioskSettlements'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/kiosk-settlements',
                    requiredPermission: 'kiosk-settlements:read:all',
                },
            ],
        },
        {
            name: 'reservations',
            label: translate('menu.reservations'),
            icon: <CalendarMonth />,
            requiredAnyPermission: [
                'reservations:read:all',
                'reservation-policies:read:all',
            ],
            children: [
                {
                    name: 'reservations-list',
                    label: translate('menu.reservationsList'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reservations',
                    requiredPermission: 'reservations:read:all',
                },
                {
                    name: 'reservations-calendar',
                    label: translate('menu.reservationsCalendar'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reservations/calendar',
                    requiredPermission: 'reservations:read:all',
                },
                {
                    name: 'reservation-policies',
                    label: translate('menu.reservationPolicies'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reservation-policies',
                    requiredPermission: 'reservation-policies:read:all',
                },
            ],
        },
        {
            name: 'fee-management',
            label: translate('menu.feeManagement'),
            icon: <AttachMoney />,
            children: [
                {
                    name: 'fee-templates',
                    label: translate('resources.fee-templates.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/fee-templates',
                    requiredPermission: 'fee-templates:read:all',
                },
                {
                    name: 'fee-schedules',
                    label: translate('resources.fee-schedules.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/fee-schedules',
                    requiredPermission: 'fee-schedules:read:all',
                },
                {
                    name: 'fee-template-changes',
                    label: translate('resources.fee-template-changes.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/fee-template-changes',
                    requiredPermission: 'fee-templates:read:all',
                },
            ],
        },
        {
            name: 'payment-settings',
            label: translate('menu.paymentSettings') || 'Payment Settings',
            icon: <AccountBalanceWallet />,
            children: [
                {
                    name: 'payment-methods',
                    label: translate('resources.payment-methods.name') || 'Payment Methods',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/payment-methods',
                    requiredPermission: 'payment-settings:read:all',
                },
                {
                    name: 'payment-gateways',
                    label: translate('resources.payment-gateways.name') || 'Payment Gateways',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/payment-gateways',
                    requiredPermission: 'payment-settings:read:all',
                },
            ],
        },
        {
            name: 'reports',
            label: translate('menu.reports') || 'Reports',
            icon: <TrendingUp />,
            children: [
                {
                    name: 'reports-dashboard',
                    label: translate('reports.dashboard.title') || 'Reports Dashboard',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports',
                    requiredAnyPermission: ALL_REPORT_READ_PERMISSIONS,
                },
                {
                    name: 'revenue-report',
                    label: translate('reports.revenue.title') || 'Revenue Analysis',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/financial/revenue',
                    requiredPermission: 'reports:revenue:read',
                },
                {
                    name: 'transactions-report',
                    label: translate('reports.transactions.title') || 'Transaction Volume',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/financial/transactions',
                    requiredPermission: 'reports:transactions:read',
                },
                {
                    name: 'kiosk-report',
                    label: translate('reports.kiosk.title') || 'Kiosk',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/financial/kiosk',
                    requiredPermission: 'reports:kiosk:read',
                },
                {
                    name: 'sessions-report',
                    label: translate('reports.sessions.title') || 'Session Analytics',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/operational/sessions',
                    requiredPermission: 'reports:sessions:read',
                },
                {
                    name: 'utilization-report',
                    label: translate('reports.utilization.title') || 'Charger Utilization',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/operational/charger-utilization',
                    requiredPermission: 'reports:charger-utilization:read',
                },
                {
                    name: 'energy-report',
                    label: translate('reports.energy.title') || 'Energy Consumption',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/operational/energy-consumption',
                    requiredPermission: 'reports:energy-consumption:read',
                },
                {
                    name: 'peak-usage-report',
                    label: translate('reports.peak.title') || 'Peak Usage Analysis',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/operational/peak-usage',
                    requiredPermission: 'reports:peak-usage:read',
                },
                {
                    name: 'user-activity-report',
                    label: translate('reports.userActivity.title') || 'User Activity',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/operational/user-activity',
                    requiredPermission: 'reports:user-activity:read',
                },
                {
                    name: 'vendor-performance-report',
                    label: translate('reports.vendorPerformance.title') || 'Vendor Performance',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/vendor/performance',
                    requiredPermission: 'reports:vendor-performance:read',
                },
                {
                    name: 'site-comparison-report',
                    label: translate('reports.siteComparison.title') || 'Site Comparison',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/reports/vendor/sites',
                    requiredPermission: 'reports:site-comparison:read',
                },
            ],
        },
        {
            name: 'logs',
            label: translate('menu.logs'),
            icon: <Description />,
            children: [
                {
                    name: 'audit-logs',
                    label: translate('resources.audit-logs.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/audit-logs',
                    requiredPermission: 'audit:read:all',
                },
                {
                    name: 'lms-logs',
                    label: translate('resources.lms-logs.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/lms-logs',
                    requiredPermission: 'lms-logs:read:all',
                },
                {
                    name: 'ocpp-logs',
                    label: translate('menu.ocppLogs') || 'OCPP Logs',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/ocpp-logs',
                    requiredPermission: 'ocpp-logs:read:all',
                },
                {
                    name: 'notification-logs',
                    label: translate('menu.notificationLogs'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/notification-logs',
                    requiredPermission: 'notifications:read:all',
                },
            ],
        },
        {
            // Shared AI Troubleshooting parent group (created here by sub-plan 14; sub-plans
            // 18/19/20 nest their own leaves under this SAME group rather than recreating it).
            name: 'ai-troubleshooting',
            label: translate('menu.aiTroubleshooting'),
            icon: <Psychology />,
            children: [
                {
                    name: 'ai-investigate',
                    label: translate('aiTroubleshooting.menu.investigate'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/ai-troubleshooting',
                    requiredPermission: 'ai_troubleshooting:use:all',
                },
                {
                    // Sub-plan 18: playbook management. Labelled via the shared aiTroubleshooting
                    // namespace (NOT a separate menu.aiPlaybooks key); gated by manage:all so it
                    // auto-hides for use-only operators (REF-08 s.5.5).
                    name: 'ai-playbooks',
                    label: translate('aiTroubleshooting.playbooks', { _: 'Playbooks' }),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/ai-playbooks',
                    requiredPermission: 'ai_troubleshooting:manage:all',
                },
                {
                    // Sub-plan 19: AI Fault Analytics. Labelled via the shared aiTroubleshooting
                    // namespace; gated by manage:all so it auto-hides for use-only operators.
                    name: 'ai-fault-analytics',
                    label: translate('aiTroubleshooting.analytics.title', { _: 'AI Fault Analytics' }),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/ai-troubleshooting/analytics',
                    requiredPermission: 'ai_troubleshooting:manage:all',
                },
                {
                    // Sub-plan 20: AI model-config admin. Labelled via the ai-model-configs resource
                    // strings; gated by manage:all so it auto-hides for use-only operators (the A/B
                    // comparison itself lives inside the investigation detail, not a menu item).
                    name: 'ai-model-configs',
                    label: translate('resources.ai-model-configs.name', { _: 'AI Model Configs' }),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/ai-model-configs',
                    requiredPermission: 'ai_troubleshooting:manage:all',
                },
            ],
        },
        {
            name: 'divider1',
            label: '',
            icon: <></>,
        },
        {
            name: 'security',
            label: translate('menu.security'),
            icon: <Security />,
            children: [
                {
                    name: 'roles',
                    label: translate('menu.rolesPermissions'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/roles',
                    requiredPermission: 'roles:read:all',
                },
                {
                    name: 'cron-monitor',
                    label: translate('menu.cronMonitor'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/cron-monitor',
                    requiredPermission: 'system:admin:all',
                },
                {
                    name: 'supabase-connections',
                    label: translate('resources.supabase-connections.name'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/supabase-connections',
                    requiredPermission: 'supabase-connections:read:all',
                },
            ],
        },
        {
            name: 'marketing',
            label: translate('menu.marketing') || 'Marketing',
            icon: <LocalOffer />,
            children: [
                {
                    name: 'banners',
                    label: translate('menu.banners') || 'Promotion Banners',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/banners',
                    requiredPermission: 'messages:read:all',
                },
                {
                    name: 'promotions',
                    label: translate('menu.promotions') || 'Promotions & Coupons',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/promotions',
                    requiredPermission: 'promotions:read:all',
                },
            ],
        },
        {
            name: 'notifications',
            label: translate('menu.notifications'),
            icon: <Notifications />,
            children: [
                {
                    name: 'messages',
                    label: translate('menu.messages') || 'Messages',
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/messages',
                    requiredPermission: 'messages:read:all',
                },
                {
                    name: 'notification-groups',
                    label: translate('menu.notificationGroups'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/notification-groups',
                    requiredPermission: 'notifications:read:all',
                },
                {
                    name: 'email-provider-routing',
                    label: translate('menu.emailProviderRouting'),
                    icon: <FiberManualRecord sx={{ fontSize: 8 }} />,
                    path: '/email-provider-routing',
                    requiredPermission: 'email_routing:read:all',
                },
            ],
        },
        {
            name: 'app-versions',
            label: translate('resources.app-versions.name'),
            icon: <SystemUpdate />,
            path: '/app-versions',
            requiredPermission: 'app-versions:read:all',
        },
        {
            name: 'settings',
            label: translate('menu.settings'),
            icon: <Settings />,
            path: '/settings',
            requiredPermission: 'settings:read:all',
        },
    ];

    const handleToggleItem = (name: string) => {
        setOpenItems(prev => 
            prev.includes(name) 
                ? prev.filter(item => item !== name)
                : [...prev, name]
        );
    };

    const isActive = (path?: string) => {
        if (!path) return false;
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const hasPermission = (permission?: string) =>
        checkPermission(permissions as string[] | null | undefined, permission);

    const itemIsVisible = (item: MenuItem): boolean => {
        if (item.hideForVendorScoped && isVendorScoped) {
            return false;
        }
        if (item.requiredAnyPermission && item.requiredAnyPermission.length > 0) {
            return item.requiredAnyPermission.some(p => hasPermission(p));
        }
        return !item.requiredPermission || hasPermission(item.requiredPermission);
    };

    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
        return items.map(item => {
            // Always show dividers
            if (item.name === 'divider1') {
                return item;
            }

            // For parent items with children, filter the children
            if (item.children) {
                const visibleChildren = item.children.filter(itemIsVisible);

                // If no children are visible, hide the parent
                if (visibleChildren.length === 0) {
                    return null;
                }

                // Return a new item with filtered children
                return { ...item, children: visibleChildren };
            }

            // For leaf items, check permission
            if (!itemIsVisible(item)) {
                return null;
            }

            return item;
        }).filter(item => item !== null) as MenuItem[];
    };

    const renderMenuItem = (item: MenuItem, depth = 0) => {
        if (item.name === 'divider1') {
            return <Divider key={item.name} sx={{ my: 2, mx: 2 }} />;
        }

        const hasChildren = item.children && item.children.length > 0;
        const isOpen = openItems.includes(item.name);
        const active = isActive(item.path);

        return (
            <React.Fragment key={item.name}>
                <ListItem
                    onClick={() => {
                        if (hasChildren && !collapsed) {
                            handleToggleItem(item.name);
                        } else if (item.path) {
                            navigate(item.path);
                            onNavigate?.();
                        }
                    }}
                    sx={{
                        mx: 1,
                        mb: 0.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        backgroundColor: active ? 'rgba(0,200,83,0.08)' : 'transparent',
                        '&:hover': {
                            backgroundColor: active ? 'rgba(0,200,83,0.12)' : '#F8F9FA',
                        },
                        pl: collapsed ? 2 : depth === 0 ? 2 : 4,
                        transition: 'all 0.2s',
                    }}
                >
                    <ListItemIcon 
                        sx={{ 
                            minWidth: collapsed ? 'auto' : 40,
                            color: active ? '#00C853' : '#606060',
                        }}
                    >
                        {collapsed ? (
                            <Tooltip title={item.label} placement="right">
                                {item.icon}
                            </Tooltip>
                        ) : (
                            item.icon
                        )}
                    </ListItemIcon>
                    
                    {!collapsed && (
                        <>
                            <ListItemText 
                                primary={item.label}
                                slotProps={{
                                    primary: {
                                        sx: {
                                            fontSize: depth === 0 ? '0.875rem' : '0.813rem',
                                            fontWeight: active ? 600 : depth === 0 ? 500 : 400,
                                            color: active ? '#00C853' : '#1A1A1A',
                                        }
                                    }
                                }}
                            />
                            {item.badge && (
                                <Chip
                                    label={item.badge}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        backgroundColor: item.badgeColor || '#E0E0E0',
                                        color: item.badgeColor ? 'white' : '#606060',
                                        fontWeight: 600,
                                    }}
                                />
                            )}
                            {hasChildren && (
                                isOpen ? <ExpandLess sx={{ color: '#909090' }} /> : <ExpandMore sx={{ color: '#909090' }} />
                            )}
                        </>
                    )}
                </ListItem>

                {!collapsed && hasChildren && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List disablePadding>
                            {item.children!.map(child => renderMenuItem(child, depth + 1))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    return (
        <Box
            sx={{
                width: collapsed ? 80 : 280,
                height: 'calc(100vh - 72px)',
                backgroundColor: '#FFFFFF',
                borderRight: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'width 0.3s',
                boxShadow: '2px 0 8px rgba(0,0,0,0.02)',
            }}
        >
            {/* Menu Header */}
            {!collapsed && (
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography 
                        variant="overline" 
                        sx={{ 
                            color: '#909090',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            letterSpacing: 1.5,
                        }}
                    >
                        {translate('menu.mainMenu')}
                    </Typography>
                </Box>
            )}

            {/* Menu Items */}
            <List sx={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                px: 1,
                '&::-webkit-scrollbar': {
                    width: '0px',
                    background: 'transparent',
                },
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
            }}>
                {filterMenuItems(menuItems).map(item => renderMenuItem(item))}
            </List>

            {!onNavigate && (
                <Box
                    sx={{
                        p: 2,
                        borderTop: '1px solid rgba(0,0,0,0.06)',
                        display: 'flex',
                        justifyContent: collapsed ? 'center' : 'flex-end',
                    }}
                >
                    <IconButton
                        onClick={handleCollapseToggle}
                        sx={{
                            backgroundColor: '#F8F9FA',
                            '&:hover': {
                                backgroundColor: '#F0F0F0',
                            },
                        }}
                    >
                        {collapsed ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};